/* screens/kitchen/registries/MenusRegistry.jsx — вкладка «Проекты меню».
   Плиточный реестр меню с экономическими метриками. Фильтр: Статус. Сортировка: имя/блюда/FC.
   ХЕНДОФФ → frontend/pages/kitchen/MenusTab.jsx. */

import React from 'react';
import { useConfirm } from '../../../app/providers/ConfirmProvider.jsx';
import { useToast } from '../../../app/providers/ToastProvider.jsx';
import { MenuGridCard, SkeletonGrid } from '../../../components/patterns/Cards.jsx';
import { EmptyState, QueryBoundary } from '../../../components/patterns/States.jsx';
import { KitchenFilterDrawer } from '../../../components/patterns/kitchen/KitchenFilterDrawer.jsx';
import { KitchenToolbar } from '../../../components/patterns/kitchen/KitchenToolbar.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { hooks } from '../../../hooks/index.jsx';
import { useRegistrySelection } from '../../../hooks/useRegistrySelection.jsx';
import { bulkDeleteMenus, listMenuBriefs } from '../../../lib/api/index.js';
import { RR } from '../../../lib/router.jsx';
import { enums as E } from '../../../lib/enums.js';

  const { useMemo, useState, useEffect } = React;
  const { useSearchParams } = RR;
  const { useFakeQuery, useDebouncedSearchParam, useDrawerStack } = hooks;

  const SORTS = [
    { value: 'name_asc', label: 'Название А–Я' },
    { value: 'name_desc', label: 'Название Я–А' },
    { value: 'dishes_desc', label: 'Больше блюд' },
    { value: 'fc_asc', label: 'Целевой FC ↑' },
  ];
  // Подписи статусов — из ЕДИНОГО словаря E.STATUS_LABEL (P2-35): опция фильтра ↔ бейдж.
  const STATUS_OPTS = ['draft', 'active', 'archived'].map((v) => ({ value: v, label: E.STATUS_LABEL[v] || v }));
  const readArr = (p, n) => { const v = p.get(n); return v ? v.split(',') : []; };

  function MenusRegistry() {
    const [params, setParams] = useSearchParams();
    const [q, setQ] = useDebouncedSearchParam('q', 300);
    const { push } = useDrawerStack();
    const [filterOpen, setFilterOpen] = useState(false);
    const confirm = useConfirm();
    const toast = useToast();

    const statuses = readArr(params, 'status');
    const sort = params.get('sort') || 'name_asc';
    const mockErr = params.get('mock_error'); const mock403 = params.get('mock_403');

    const setParam = (name, arr) => setParams((prev) => {
      const sp = new URLSearchParams(prev);
      if (Array.isArray(arr) ? arr.length : arr) sp.set(name, Array.isArray(arr) ? arr.join(',') : arr);
      else sp.delete(name);
      return sp;
    });
    const resetFilters = () => setParams((prev) => {
      const sp = new URLSearchParams(prev); sp.delete('status'); return sp;
    });

    const query = useFakeQuery('menus', () => listMenuBriefs(), [mockErr, mock403]);
    useEffect(() => {
      const h = () => query.reload();
      window.addEventListener('kitchen:menus-changed', h);
      return () => window.removeEventListener('kitchen:menus-changed', h);
    }, [query]);

    const list = useMemo(() => {
      const rows = query.data || [];
      const needle = (q || '').trim().toLowerCase();
      let filtered = rows.filter((it) => {
        if (needle && !it.name.toLowerCase().includes(needle)) return false;
        if (statuses.length && !statuses.includes(it.status)) return false;
        return true;
      });
      filtered = [...filtered].sort((a, b) => {
        switch (sort) {
          case 'name_desc': return b.name.localeCompare(a.name, 'ru');
          case 'dishes_desc': return (b.dishes_count || 0) - (a.dishes_count || 0);
          case 'fc_asc': return (a.target_food_cost_pct || 0) - (b.target_food_cost_pct || 0);
          default: return a.name.localeCompare(b.name, 'ru');
        }
      });
      return filtered;
    }, [query.data, q, statuses.join(','), sort]);

    const hasFilters = statuses.length > 0 || !!(q && q.trim());

    // F41: множественный выбор (bulk-удаление меню).
    const sel = useRegistrySelection(list, {});

    const bulkDelete = async () => {
      const ids = [...sel.selected];
      if (!ids.length) return;
      const ok = await confirm({
        title: `Удалить выбранные меню (${ids.length})?`,
        body: `Будет удалено ${ids.length} проектов меню. Действие необратимо.`,
        confirmLabel: 'Удалить', tone: 'danger',
        checkboxLabel: 'Также удалить блюда, входившие только в эти меню (вместе с историей версий)',
        checkboxDefault: false,
      });
      if (!ok) return;
      const cascade = !!(ok && ok.checked);
      sel.setBusy(true);
      try {
        let deletedN = 0; let dishesN = 0; let skippedN = 0;
        for (let i = 0; i < ids.length; i += 200) {
          const r = await bulkDeleteMenus(ids.slice(i, i + 200), { cascade });
          if (!r.ok) throw new Error((r.data && r.data.detail) || 'bulk failed');
          deletedN += ((r.data && r.data.deleted) || []).length;
          dishesN += (r.data && r.data.deleted_dishes) || 0;
          skippedN += ((r.data && r.data.skipped) || []).length;
        }
        let msg = `Меню удалено: ${deletedN}`;
        if (cascade && dishesN) msg += `, блюд: ${dishesN}`;
        if (cascade && skippedN) msg += `; пропущено блюд: ${skippedN}`;
        toast.show(msg, 'success');
        sel.exit();
        query.reload();
        window.dispatchEvent(new CustomEvent('kitchen:menus-changed'));
        if (cascade) window.dispatchEvent(new CustomEvent('kitchen:ttks-changed'));
      } catch (e) {
        toast.show((e && e.message) || 'Не удалось удалить выбранные', 'danger');
      } finally { sel.setBusy(false); }
    };

    const toCard = (it) => ({
      status: it.status, name: it.name,
      dishes: it.dishes_count, avg_check: it.average_check,
      avg_cost: it.average_cost_per_portion, fc: it.target_food_cost_pct,
    });

    return (
      <div className="registry">
        {sel.selectMode ? (
          <div className="registry__bulkbar">
            <span className="t-body">Выбрано: {sel.count}</span>
            <Button size="sm" variant="secondary" onClick={sel.selectAll}>
              {sel.allSelected ? 'Снять всё' : 'Выбрать все'}
            </Button>
            <div className="grow" />
            <Button size="sm" variant="danger" iconLeft="trash" disabled={!sel.count || sel.busy} loading={sel.busy} onClick={bulkDelete}>Удалить</Button>
            <Button size="sm" variant="ghost" onClick={sel.exit} disabled={sel.busy}>Готово</Button>
          </div>
        ) : (
          <KitchenToolbar
            search={q} onSearch={setQ} searchPlaceholder="Поиск по меню…"
            onFilter={() => setFilterOpen(true)} activeFilterCount={statuses.length}
            sortOptions={SORTS} sortValue={sort} onSort={(v) => setParam('sort', v)}
            actionLabel="Создать проект меню" onAction={() => push('menu', 'new')} />
        )}
        {!sel.selectMode && list.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button size="sm" variant="ghost" onClick={() => sel.setSelectMode(true)}>Выбрать несколько</Button>
          </div>
        )}

        <QueryBoundary
          loading={query.loading} error={query.error} onRetry={query.reload}
          empty={!query.loading && !query.error && list.length === 0}
          skeleton={<SkeletonGrid count={4} />}
          emptyEl={hasFilters
            ? <EmptyState serif={false} icon="search" title="Ничего не найдено"
                body="Под выбранные фильтры проектов меню нет."
                action={<Button variant="secondary" onClick={resetFilters}>Сбросить фильтры</Button>} />
            : <EmptyState icon="layers" title="Соберите первый проект меню"
                body="Меню собирается из ТТК под концепцию и сезон: средний чек, целевой food cost, оформление и экспорт."
                action={<Button variant="primary" iconLeft="plus" onClick={() => push('menu', 'new')}>Создать проект меню</Button>} />}>
          <div className="tile-grid">
            {list.map((it) => (
              <MenuGridCard key={it.id} item={toCard(it)} onClick={() => push('menu', it.id)}
                selectable={sel.selectMode} selected={sel.selected.has(it.id)}
                onToggleSelect={() => sel.toggle(it.id)} />
            ))}
          </div>
        </QueryBoundary>

        <KitchenFilterDrawer
          open={filterOpen} onClose={() => setFilterOpen(false)}
          value={{ status: statuses }}
          onChange={(d) => setParam('status', d.status || [])}
          sections={[{ key: 'status', title: 'Статус', options: STATUS_OPTS }]} />
      </div>
    );
  }

export { MenusRegistry };
